// config/api.config.ts

declare global {
    interface Window {
        ENV_CONFIG: {
            BACKEND_API_URL: string;
            AUTH0_DOMAIN: string;
            AUTH0_CLIENT_ID: string;
            AUTH0_AUDIENCE: string;
            GOOGLE_OAUTH_CLIENT_ID: string;
            STRIPE_PUBLISHABLE_KEY: string;
        };
    }
}

const DEFAULT_CONFIG = {
    BACKEND_API_URL: 'https://api.orangefennec.com',
    AUTH0_DOMAIN: 'login.orangefennec.com',
    AUTH0_CLIENT_ID: 'hoGgNNUDHaAiURJpficPEtZ08PiXQonD',
    AUTH0_AUDIENCE: 'https://api.orangefennec.com',
    GOOGLE_OAUTH_CLIENT_ID: '49411765617-dfperusmdurvhj6l35r49s6qcd0kue8j.apps.googleusercontent.com',
    STRIPE_PUBLISHABLE_KEY: 'pk_test_51Qo7gxAEso448Xmt19MzMa9BrmqLd3k90uGkmttVwi5PVx3T5nnUdtqoVdwEdBY70d3rmYXVBIC8xd6mGRmV4iVb00DABUKnlR'
} as const;

const getConfig = () => {
    // Check for server-side rendering
    if (typeof window === 'undefined') {
        return {
            backendUrl: import.meta.env.VITE_BACKEND_API_URL || DEFAULT_CONFIG.BACKEND_API_URL,
            auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN || DEFAULT_CONFIG.AUTH0_DOMAIN,
            auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID || DEFAULT_CONFIG.AUTH0_CLIENT_ID,
            auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE || DEFAULT_CONFIG.AUTH0_AUDIENCE,
            googleOAuthClientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || DEFAULT_CONFIG.GOOGLE_OAUTH_CLIENT_ID,
            stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || DEFAULT_CONFIG.STRIPE_PUBLISHABLE_KEY
        };
    }

    // Check if window.ENV_CONFIG exists and has defined values
    if (window.ENV_CONFIG) {
        console.log('Using window.ENV_CONFIG for application configuration', {
            domain: window.ENV_CONFIG.AUTH0_DOMAIN,
            clientId: window.ENV_CONFIG.AUTH0_CLIENT_ID
        });

        return {
            backendUrl: window.ENV_CONFIG.BACKEND_API_URL || DEFAULT_CONFIG.BACKEND_API_URL,
            auth0Domain: window.ENV_CONFIG.AUTH0_DOMAIN || DEFAULT_CONFIG.AUTH0_DOMAIN,
            auth0ClientId: window.ENV_CONFIG.AUTH0_CLIENT_ID || DEFAULT_CONFIG.AUTH0_CLIENT_ID,
            auth0Audience: window.ENV_CONFIG.AUTH0_AUDIENCE || DEFAULT_CONFIG.AUTH0_AUDIENCE,
            googleOAuthClientId: window.ENV_CONFIG.GOOGLE_OAUTH_CLIENT_ID || DEFAULT_CONFIG.GOOGLE_OAUTH_CLIENT_ID,
            stripePublishableKey: window.ENV_CONFIG.STRIPE_PUBLISHABLE_KEY || DEFAULT_CONFIG.STRIPE_PUBLISHABLE_KEY,
        };
    }

    // Fallback to environment variables
    console.log('Falling back to import.meta.env for configuration');
    return {
        backendUrl: import.meta.env.VITE_BACKEND_API_URL || DEFAULT_CONFIG.BACKEND_API_URL,
        auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN || DEFAULT_CONFIG.AUTH0_DOMAIN,
        auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID || DEFAULT_CONFIG.AUTH0_CLIENT_ID,
        auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE || DEFAULT_CONFIG.AUTH0_AUDIENCE,
        googleOAuthClientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || DEFAULT_CONFIG.GOOGLE_OAUTH_CLIENT_ID,
        stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || DEFAULT_CONFIG.STRIPE_PUBLISHABLE_KEY
    };
};

const config = getConfig();

export const API_BASE_URL = config.backendUrl;
export const AUTH0_CONFIG = {
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    audience: config.auth0Audience
};
export const GOOGLE_OAUTH_CLIENT_ID = config.googleOAuthClientId;

export const API_ENDPOINTS = {
    AUTH: {
        LOGIN: `${API_BASE_URL}/api/auth/login`,
        SIGNUP: `${API_BASE_URL}/api/auth/signup`,
        RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password/admin`,
        CHANGE_PASSWORD: `${API_BASE_URL}/api/auth/change-password`,
        CHECK_PASSWORD_STATUS: `${API_BASE_URL}/api/auth/password-status`,
        CHECK_ORG_SETTINGS: `${API_BASE_URL}/api/auth/check-org-settings`,
        CHECK_LOGIN_OPTIONS: `${API_BASE_URL}/api/auth/check-login-options`,
        LOGOUT: `${API_BASE_URL}/api/auth/logout`,
        AUTH0: {
            VERIFY_TOKEN: `${API_BASE_URL}/api/auth/verify-auth0-token`,
            REFRESH_TOKEN: `${API_BASE_URL}/api/auth/refresh-token`,
            VALIDATE_SESSION: `${API_BASE_URL}/api/auth/validate-session`,
        }
    },
    ORGANIZATIONS: {
        LIST: `${API_BASE_URL}/api/organizations`,
        CREATE: `${API_BASE_URL}/api/organizations`,
        GET: `${API_BASE_URL}/api/organizations`,
        UPDATE: `${API_BASE_URL}/api/organizations`,
        DELETE: `${API_BASE_URL}/api/organizations`,
        DETAILS: `${API_BASE_URL}/api/organizations/details`,
        GET_ADMINS: `${API_BASE_URL}/api/organizations`,
        GET_USERS: `${API_BASE_URL}/api/organizations`,
        CLOUD_RESOURCES: `${API_BASE_URL}/api/organizations/:id/cloud-resources`,
        UPDATE_CLOUD_QUOTA: `${API_BASE_URL}/api/organizations/:id/cloud-resources/quota`,
        UPDATE_CLOUD_STATUS: `${API_BASE_URL}/api/organizations/:id/cloud-resources/status`,
        AUTH_SETTINGS: {
            GET: (id: string) => `${API_BASE_URL}/api/organizations/${id}/auth-settings`,
            UPDATE: (id: string) => `${API_BASE_URL}/api/organizations/${id}/auth-settings`,
            SECURITY: {
                GET: (id: string) => `${API_BASE_URL}/api/organizations/${id}/security-settings`,
                UPDATE: (id: string) => `${API_BASE_URL}/api/organizations/${id}/security-settings`,
                VALIDATE: (id: string) => `${API_BASE_URL}/api/organizations/${id}/security-settings/validate`,
                GET_DEFAULTS: (id: string) => `${API_BASE_URL}/api/organizations/${id}/security-settings/defaults`
            }
        },
        CHECK_ORG_SETTINGS: `${API_BASE_URL}/api/organizations/auth/check-org-settings`,
        IS_CONSUMER: `${API_BASE_URL}/api/organizations/:id/is-consumer`,
    },
    PROFILE: {
        GET: `${API_BASE_URL}/api/profile`,
        UPDATE: `${API_BASE_URL}/api/profile`,
        DELETE: `${API_BASE_URL}/api/profile`
    },
    DATA_SOURCE: {
        LIST: `${API_BASE_URL}/api/data-sources/list`,
        RENAME: `${API_BASE_URL}/api/data-sources/rename`,
        DELETE: `${API_BASE_URL}/api/data-sources/delete`,
        DETAILS: `${API_BASE_URL}/api/data-sources/details`,
        GROUP_ACCESS: `${API_BASE_URL}/api/data-sources/details`,
        CREATE_FOLDER: `${API_BASE_URL}/api/data-sources/folders`,
        LIST_FOLDERS: `${API_BASE_URL}/api/data-sources/folders`,
        LIST_FOLDERS_HIERARCHY: `${API_BASE_URL}/api/data-sources/folders/hierarchy`,
        DELETE_FOLDER: `${API_BASE_URL}/api/data-sources/folders`,
        MOVE_TO_FOLDER: `${API_BASE_URL}/api/data-sources/move-to-folder`,
        FOLDER_DETAILS: `${API_BASE_URL}/api/data-sources/folders/:id`,
        MOVE_FOLDER: `${API_BASE_URL}/api/data-sources/folders/move`,
        RENAME_FOLDER: `${API_BASE_URL}/api/data-sources/folders/:id/rename`,

        WEB_SOURCES: {
            LIST: `${API_BASE_URL}/api/data-sources/web/list`,
            GET: `${API_BASE_URL}/api/data-sources/web/:id`,
            CREATE: `${API_BASE_URL}/api/data-sources/web/create`,
            UPDATE: `${API_BASE_URL}/api/data-sources/web/:id/update`,
            DELETE: `${API_BASE_URL}/api/data-sources/web/:id/delete`,
            DETAILS: `${API_BASE_URL}/api/data-sources/web/:id/details`,
            STATUS: `${API_BASE_URL}/api/data-sources/web/:id/status`,
            SYNC: `${API_BASE_URL}/api/data-sources/web/:id/sync`,
            USAGE: `${API_BASE_URL}/api/data-sources/web/:id/usage`
        },

        API_SOURCES: {
            LIST: `${API_BASE_URL}/api/data-sources/api/list`,
            GET: `${API_BASE_URL}/api/data-sources/api/:id`,
            CREATE: `${API_BASE_URL}/api/data-sources/api/create`,
            UPDATE: `${API_BASE_URL}/api/data-sources/api/:id/update`,
            DELETE: `${API_BASE_URL}/api/data-sources/api/:id/delete`,
            DETAILS: `${API_BASE_URL}/api/data-sources/api/:id/details`,
            STATUS: `${API_BASE_URL}/api/data-sources/api/:id/status`,
            TEST: `${API_BASE_URL}/api/data-sources/api/:id/test`,
            SYNC: `${API_BASE_URL}/api/data-sources/api/:id/sync`,
            USAGE: `${API_BASE_URL}/api/data-sources/api/:id/usage`
        }
    },
    RAG: {
        BASE: `${API_BASE_URL}/api/rag`,
        LIST: `${API_BASE_URL}/api/rag`,
        GET: `${API_BASE_URL}/api/rag`,
        UPDATE: `${API_BASE_URL}/api/rag`,
        DELETE: `${API_BASE_URL}/api/rag`,
        TOGGLE_ENABLED: `${API_BASE_URL}/api/rag/toggle-enabled`,
        GET_STATE: `${API_BASE_URL}/api/rag/state`,
        GROUP_ACCESS: `${API_BASE_URL}/api/rag/group-access`,
        ACCESS_CHECK: `${API_BASE_URL}/api/rag/access-check`,
        INTEGRATIONS: `${API_BASE_URL}/api/rag/integrations`,
    },
    CHAT: {
        BASE: `${API_BASE_URL}/api/chat`,
        SESSIONS: {
            LIST: (applicationId: string, page?: number, limit?: number) =>
                `${API_BASE_URL}/api/chat/sessions/${applicationId}${
                    page ? `?page=${page}&limit=${limit || 10}` : ''
                }`,
            CREATE: (applicationId: string) =>
                `${API_BASE_URL}/api/chat/sessions/${applicationId}`,
            DELETE: (applicationId: string, sessionId: string) =>
                `${API_BASE_URL}/api/chat/sessions/${applicationId}/${sessionId}`,
            GET: (applicationId: string, sessionId: string) =>
                `${API_BASE_URL}/api/chat/sessions/${applicationId}/${sessionId}`
        },
        MESSAGES: {
            LIST: (applicationId: string, sessionId: string) =>
                `${API_BASE_URL}/api/chat/messages/${applicationId}/sessions/${sessionId}`,
            SEND: (applicationId: string, sessionId: string) =>
                `${API_BASE_URL}/api/chat/messages/${applicationId}/sessions/${sessionId}`,
        }
    },
    INDEXES: {
        CREATE: `${API_BASE_URL}/api/indexes`,
        LIST: `${API_BASE_URL}/api/indexes`,
        GET: `${API_BASE_URL}/api/indexes`,
        UPDATE: `${API_BASE_URL}/api/indexes`,
        DELETE: `${API_BASE_URL}/api/indexes`,
        DEPLOY: `${API_BASE_URL}/api/indexes/deploy`,
        UPDATE_STATUS: `${API_BASE_URL}/api/indexes/status`,
        GROUP_ACCESS: `${API_BASE_URL}/api/indexes/group-access`,
        USAGE: `${API_BASE_URL}/api/indexes/usage`,
    },
    FILES: {
        UPLOAD_URL: `${API_BASE_URL}/api/files/upload-url`,
        UPLOAD_COMPLETE: `${API_BASE_URL}/api/files/upload-complete`,
        LIST: `${API_BASE_URL}/api/files/list`,
        DELETE: `${API_BASE_URL}/api/files`,
        DOWNLOAD: `${API_BASE_URL}/api/files/download`,
        GET: `${API_BASE_URL}/api/files`,
        UPDATE_ACCESS: `${API_BASE_URL}/api/files/access`,
        SHARED_WITH_ME: `${API_BASE_URL}/api/files/shared`,
        GROUP_ACCESS: `${API_BASE_URL}/api/files/group-access`,
    },
    DRIVE: {
        LIST_FILES: `${API_BASE_URL}/api/drive/list`,
        SYNC_FILES: `${API_BASE_URL}/api/drive/sync`,
        GET_FILE: `${API_BASE_URL}/api/drive/files`,
        SYNCED: `${API_BASE_URL}/api/drive/synced`
    },
    GROUPS: {
        LIST: `${API_BASE_URL}/api/groups`,
        CREATE: `${API_BASE_URL}/api/groups`,
        GET: `${API_BASE_URL}/api/groups`,
        UPDATE: `${API_BASE_URL}/api/groups`,
        DELETE: `${API_BASE_URL}/api/groups`,
        ADD_USERS: `${API_BASE_URL}/api/groups/add-users`,
        REMOVE_USERS: `${API_BASE_URL}/api/groups/remove-users`,
        LIST_USERS: `${API_BASE_URL}/api/groups`,
        ADD_MEMBER: `${API_BASE_URL}/api/groups/add-member`,
        REMOVE_MEMBER: `${API_BASE_URL}/api/groups/remove-member`,
        LIST_FILES: `${API_BASE_URL}/api/groups/files`,
    },
    USERS: {
        LIST: `${API_BASE_URL}/api/users`,
        ME: `${API_BASE_URL}/api/users/me`,
        CREATE: `${API_BASE_URL}/api/users`,
        INVITE: `${API_BASE_URL}/api/users/invite`,
        RESET_PASSWORD_EMAIL: `${API_BASE_URL}/api/users/reset-password-email`,
        GET: `${API_BASE_URL}/api/users`,
        UPDATE: `${API_BASE_URL}/api/users`,
        DELETE: `${API_BASE_URL}/api/users`,
        GET_ROLE: `${API_BASE_URL}/api/users/:id/role`,
        LIST_GROUPS: `${API_BASE_URL}/api/users/:id/groups`,
        ADD_TO_GROUPS: `${API_BASE_URL}/api/users/add-to-groups`,
        REMOVE_FROM_GROUPS: `${API_BASE_URL}/api/users/remove-from-groups`,
        LIST_FILES: `${API_BASE_URL}/api/users/files`,
        ACCEPT_INVITATION: `${API_BASE_URL}/api/users/accept-invitation`,
        VALIDATE_INVITATION: `${API_BASE_URL}/api/users/validate-invitation`,
        VALIDATE_RESET_TOKEN: `${API_BASE_URL}/api/users/validate-reset-token`,
        COMPLETE_RESET_PASSWORD: `${API_BASE_URL}/api/users/complete-reset-password`,
    },
    BILLING: {
        PLANS: `${API_BASE_URL}/api/billing/plans`,
        SUBSCRIPTION: {
            GET: `${API_BASE_URL}/api/billing/subscription`,
            UPDATE: `${API_BASE_URL}/api/billing/subscription`,
            CANCEL: `${API_BASE_URL}/api/billing/subscription/cancel`,
            UPGRADE: `${API_BASE_URL}/api/billing/subscription/upgrade`,
            DOWNGRADE: `${API_BASE_URL}/api/billing/subscription/downgrade`,
            CANCEL_DOWNGRADE: `${API_BASE_URL}/api/billing/subscription/cancel-downgrade`,
            PENDING_DOWNGRADE: `${API_BASE_URL}/api/billing/subscription/pending-downgrade`,
            PENDING_CHANGES: `${API_BASE_URL}/api/billing/subscription/pending-changes`,
            CANCEL_CANCELLATION: `${API_BASE_URL}/api/billing/subscription/cancel-cancellation`,
        },
        USAGE: {
            CURRENT: `${API_BASE_URL}/api/billing/usage/current`,
            LIMITS: `${API_BASE_URL}/api/billing/usage/limits`
        },
        WEBHOOK: `${API_BASE_URL}/api/billing/webhook`,
        PAYMENT_METHODS: {
            LIST: `${API_BASE_URL}/api/billing/payment-methods`,
            UPDATE: `${API_BASE_URL}/api/billing/payment-methods`,
            ADD: `${API_BASE_URL}/api/billing/payment-methods`,
            DELETE: `${API_BASE_URL}/api/billing/payment-methods`,
            SET_DEFAULT: `${API_BASE_URL}/api/billing/payment-methods/default`,
        },
        ADDRESS: {
            GET: `${API_BASE_URL}/api/billing/address`,
            UPDATE: `${API_BASE_URL}/api/billing/address`
        },
        INVOICES: {
            LIST: `${API_BASE_URL}/api/billing/invoices`,
            UPCOMING: `${API_BASE_URL}/api/billing/invoices/upcoming`,
            DOWNLOAD: `${API_BASE_URL}/api/billing/invoices/download`
        }
    },
    SUPPORT: {
        CREATE_TICKET: `${API_BASE_URL}/api/support/ticket`,
        LIST_TICKETS: `${API_BASE_URL}/api/support/tickets`,
        GET_TICKET: `${API_BASE_URL}/api/support/ticket/:id`,
    },
} as const;

// Helper functions
export const buildUrl = (baseUrl: string, id: string) => `${baseUrl}/${id}`;
export const buildOrgUrl = (baseUrl: string, orgId: string) => `${baseUrl}/organizations/${orgId}`;
export const buildGroupUrl = (baseUrl: string, groupId: string) => `${baseUrl}/groups/${groupId}`;

export const STRIPE_PUBLISHABLE_KEY = config.stripePublishableKey;

// Helper types
export interface APIEndpoint {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

export const createEndpoint = (url: string, method: APIEndpoint['method']): APIEndpoint => ({
    url,
    method
});

export const buildIsConsumerUrl = (orgId: string): string =>
    API_ENDPOINTS.ORGANIZATIONS.IS_CONSUMER.replace(':id', orgId);